/**
 * MobileTradeSheet — FRAME 4 (two-team detail) + FRAME A (three-team cycle).
 * DD-MOBILE-OPTIMIZER-0724. Bottom sheet.
 *
 * Two-team: metric strip · send/receive · scenario band · sweetener.
 * Three-team (Frame A): the cycle rendered as a vertical chain on one rail (loop closes back
 * to YOU), the two counterparty_gains kept neutral/subordinate to the user's net, and
 * acceptance as an explicit product (acceptance_factors → "A 67% × B 67% = 45%"). Binds to the
 * Phase 2 fields: legs[].from/to_team_name + is_user, counterparty_gains, counterparty_names,
 * acceptance_factors, user_team_id, sensitivity (the coherent net_value+followup band).
 */
import React from 'react';
import {
  C, SP, FS, R, PRIMARY, BORDER, BORDER_SOFT, NUM, withAlpha, sectionHeaderStyle, pillStyle, riskColor,
} from './optimizerTokens';
import MobilePlayerLink from '../../components/MobilePlayerLink';
import MobileCategoryEffect from './MobileCategoryEffect';

// DD-RO-MOBILE-TRADE-MODAL-0728: the WHOLE trade's per-category detail — main swap + required backfill,
// folded — from the corrected reprojection the FA-swap modal uses (repair.user.true_category_impact =
// score_value_overlay_by_category). Every category shows its raw projected swing AND its standings-point
// impact, so a category whose RANK didn't move no longer reads as a bare "0".
const tradeCategoryRows = (pkg) => ((pkg.repair || {}).user || {}).true_category_impact || [];

// DD-RO-ITEM3-EXECUTE-0729: SEPARATED per-swap sections from the enriched swap_chain. Each swap (a sent
// player ↔ the received player OR the ONE committed backfill that fills his slot) is its OWN section, header
// "[out] → [in]", with the per-category effect across ONLY that swap's relevant cats (pitching for P↔P,
// hitting for a hitter swap). Replaces the aggregate true_category_impact view + the follow-up add-ons menu.
const swapLabel = (step) => {
  const o = step.out?.player_name;
  const i = step.in?.player_name;
  if (o && i) return `${o} → ${i}`;
  if (o && step.kind === 'drop') return `${o} → dropped`;
  if (o) return `${o} → (slot covered)`;
  if (i) return `＋ ${i}${step.kind === 'backfill' ? ' (backfill)' : ''}`;
  return '—';
};
// DD-RO-MOBILECATEGORYEFFECT-CRASH-0729: the enriched swap_chain carries category_impact as a LIST of rows;
// a PRE-ENRICHMENT backend (not yet deployed) still carries the old {category: points} DICT. Normalize both to
// the row shape MobileCategoryEffect expects, so a deploy-ordering skew renders (not white-screens).
const swapImpactRows = (ci) => (Array.isArray(ci) ? ci
  : (ci && typeof ci === 'object')
    ? Object.entries(ci).map(([category, pts]) => ({ category, pts_delta: pts }))
    : []);

function SwapSections({ pkg }) {
  const chain = (pkg.swap_chain || []).filter((s) => s.out || s.in);
  // fallback: an older payload without swap_chain → the aggregate view, so the modal never blanks.
  if (!chain.length) return <MobileCategoryEffect rows={tradeCategoryRows(pkg)} testid="trade-category-effect" />;
  return (
    <div data-testid="swap-sections">
      {chain.map((step, idx) => (
        <MobileCategoryEffect key={idx} rows={swapImpactRows(step.category_impact)} title={swapLabel(step)}
          outProj={step.out_proj} inProj={step.in_proj}
          testid="swap-section" emptyLabel="No category move on this swap." />
      ))}
    </div>
  );
}

function Shell({ testid, title, pills, onClose, children, footer }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
         data-testid={testid} role="dialog" aria-modal="true">
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: withAlpha(C.black || '#000', 0.55) }} />
      <div style={{ position: 'relative', background: C.neutral[900], borderTop: BORDER, maxHeight: '86vh', display: 'flex', flexDirection: 'column', ...NUM }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: `7px 0 5px` }}>
          <div style={{ width: 36, height: 3, borderRadius: R.none, background: C.neutral[600] }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: `0 ${SP.sm} 9px`, gap: SP.xs }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: FS.base, fontWeight: 900, color: C.white }}>{title}</div>
            {pills && <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>{pills}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="trade-sheet-close"
            style={{ width: 26, height: 26, border: BORDER, borderRadius: R.none, background: 'none', color: C.neutral[400], cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', scrollbarWidth: 'none', padding: `0 ${SP.sm} ${SP.sm}` }}>{children}</div>
        {footer}
      </div>
      <style>{`[data-testid="${testid}"] ::-webkit-scrollbar{display:none;}`}</style>
    </div>
  );
}

const Metric = ({ label, value, color }) => (
  <div style={{ flex: 1, textAlign: 'center', padding: `6px 2px`, borderLeft: BORDER_SOFT }}>
    <div style={{ fontSize: FS['2xs'], color: C.neutral[500], textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: FS.base, fontWeight: 900, color, ...NUM }}>{value}</div>
  </div>
);

// DD-MOBILE-OPTIMIZER-ACTIONS-0724: "Open in Trade Center" deep-links the package (two- OR
// three-team) into the existing Trade Center flow, pre-populated — reuses the parent's
// navigation + the Trade Center's initialPackage prefill (Rule 11, no parallel proposal path).
// §4.5.21: rendered only when a handler is available; otherwise it does not render.
const Footer = ({ onClose, onOpen }) => (
  <div style={{ display: 'flex', borderTop: BORDER, background: C.neutral[900] }}>
    <button type="button" onClick={onClose}
      style={{ flex: 1, textAlign: 'center', padding: `13px 0`, color: C.neutral[400], fontWeight: 700, fontSize: FS.xs, background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
    {onOpen && (
      <button type="button" onClick={onOpen} data-testid="open-trade-center"
        style={{ flex: 1.4, textAlign: 'center', padding: `13px 0`, color: C.neutral[950], fontWeight: 800, fontSize: FS.xs, background: PRIMARY, borderLeft: BORDER, border: 'none', cursor: 'pointer' }}>
        Open in Trade Center →
      </button>
    )}
  </div>
);

const pct = (p) => (p != null ? `${Math.round(p * 100)}%` : '—');

export default function MobileTradeSheet({ sequence, teamId, onClose, onOpenTradeCenter, onPlayerClick }) {
  if (!sequence) return null;
  const pkg = sequence.trade_package || {};
  const openTC = onOpenTradeCenter ? () => onOpenTradeCenter(pkg) : null;
  return pkg.trade_type === 'three_team'
    ? <ThreeTeam sequence={sequence} pkg={pkg} onClose={onClose} onOpen={openTC} onPlayerClick={onPlayerClick} />
    : <TwoTeam sequence={sequence} pkg={pkg} onClose={onClose} onOpen={openTC} onPlayerClick={onPlayerClick} />;
}

/** FRAME 4 — two-team detail. */
function TwoTeam({ sequence, pkg, onClose, onOpen, onPlayerClick }) {
  const sens = sequence.sensitivity || {};
  const send = (pkg.user_sends?.players || [])[0] || {};
  const recv = (pkg.user_receives?.players || [])[0] || {};
  const shs = sectionHeaderStyle(PRIMARY);
  const expected = sens.expected_gain ?? ((pkg.net_value ?? 0) + (sequence.followup_standings_gain ?? 0));
  return (
    <Shell testid="trade-sheet" onClose={onClose}
      title="Trade"
      pills={sens.risk_display_label && <span style={pillStyle(riskColor(sens.risk_profile), 0.16)}>{sens.risk_display_label}</span>}
      footer={<Footer onClose={onClose} onOpen={onOpen} />}>
      <div style={{ display: 'flex', borderTop: BORDER, borderBottom: BORDER }} data-testid="metric-strip">
        <div style={{ flex: 1, textAlign: 'center', padding: `6px 2px` }}>
          <div style={{ fontSize: FS['2xs'], color: C.neutral[500], textTransform: 'uppercase' }}>Net</div>
          <div style={{ fontSize: FS.base, fontWeight: 900, color: pkg.net_value >= 0 ? C.success : C.error, ...NUM }}>{pkg.net_value >= 0 ? `+${pkg.net_value}` : pkg.net_value}</div>
        </div>
        <Metric label="Follow-up" value={sequence.followup_standings_gain != null ? `+${sequence.followup_standings_gain}` : '—'} color={C.white} />
        <Metric label="Accept" value={pct(pkg.acceptance_probability)} color={pkg.acceptance_probability >= 0.5 ? C.success : C.warning} />
      </div>

      <div style={{ display: 'flex', gap: SP.xs, paddingTop: SP.sm }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FS['2xs'], fontWeight: 800, color: C.error, textTransform: 'uppercase' }}>You Send</div>
          <MobilePlayerLink mlbId={send.player_id} name={send.player_name || '—'} onClick={onPlayerClick}
            style={{ fontSize: FS.sm, fontWeight: 700, color: C.white }} />
          <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{[(send.eligibility_display || send.position), send.contract_salary != null ? `$${send.contract_salary}` : null].filter(Boolean).join(' · ')}</div>
        </div>
        <div style={{ width: 1, background: withAlpha(C.neutral[700], 0.5) }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: FS['2xs'], fontWeight: 800, color: C.success, textTransform: 'uppercase' }}>You Receive</div>
          <MobilePlayerLink mlbId={recv.player_id} name={recv.player_name || '—'} onClick={onPlayerClick}
            style={{ fontSize: FS.sm, fontWeight: 700, color: C.white }} />
          <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{[(recv.eligibility_display || recv.position), recv.contract_salary != null ? `$${recv.contract_salary}` : null].filter(Boolean).join(' · ')}</div>
        </div>
      </div>

      {/* DD-RO-ITEM3-EXECUTE-0729: SEPARATED per-swap sections (each swap its own header + relevant cats),
          replacing the aggregate whole-trade view AND the follow-up add-ons menu. */}
      <SwapSections pkg={pkg} />

      {/* scenario band — the coherent net_value+followup band (CLOSE Phase 4 fix) */}
      <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>Scenario</span></div>
      <div style={{ display: 'flex', borderTop: BORDER, borderBottom: BORDER }} data-testid="scenario-band">
        <ScenarioCell label="Downside" value={sens.downside_gain} color={C.error} />
        <ScenarioCell label="Expected" value={expected} color={C.white} bordered />
        <ScenarioCell label="Upside" value={sens.upside_gain} color={C.success} bordered />
      </div>
      {sens.risk_explanation && <div style={{ fontSize: FS.xs, color: C.neutral[400], fontStyle: 'italic', marginTop: 6 }}>{sens.risk_explanation}</div>}

      {(sens.sweetener_suggestions || []).length > 0 && (
        <>
          <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>Make it likelier</span></div>
          {sens.sweetener_suggestions.slice(0, 2).map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: SP.xs, borderTop: BORDER, padding: `7px 0` }} data-testid="sweetener">
              <span style={pillStyle(C.info, 0.14)}>{s.asset_type}</span>
              <span style={{ flex: 1, fontSize: FS.xs, color: C.neutral[300] }}>{s.add_asset}</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: FS.xs, fontWeight: 800, color: C.success, ...NUM }}>→ {pct(s.new_acceptance)}</div>
                <div style={{ fontSize: FS['2xs'], color: C.neutral[500], ...NUM }}>costs {s.cost_to_you}</div>
              </div>
            </div>
          ))}
        </>
      )}
    </Shell>
  );
}

const ScenarioCell = ({ label, value, color, bordered }) => (
  <div style={{ flex: 1, textAlign: 'center', padding: `7px 2px`, borderLeft: bordered ? BORDER_SOFT : 'none' }}>
    <div style={{ fontSize: FS['2xs'], color, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: FS.lg, fontWeight: 900, color, ...NUM }}>
      {value != null ? (value >= 0 ? `+${Math.round(value * 10) / 10}` : Math.round(value * 10) / 10) : '—'}
    </div>
  </div>
);

// DD-RO-BUGS5-0725 B2 — SHOW THE REPAIR on mobile. The metric strip's "Follow-up" cell shows only the
// aggregate standings number; the desktop OptimizerTradeDetail lists the actual pickup/drop the deal
// forces to keep the roster legal (sequence.follow_up_moves[]). Mobile omitted the list entirely, so a
// user could not see WHAT the repair does — only that some number moved. This renders the same fields
// desktop does: player_in {player_id, player_name, position}, player_out, standings_delta, category_impact.
const FollowUpMoves = ({ moves, onPlayerClick }) => {
  const list = moves || [];
  if (list.length === 0) return null;
  const shs = sectionHeaderStyle(PRIMARY);
  return (
    <>
      <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>Follow-Up Moves</span>
        <span style={{ marginLeft: 'auto', fontSize: FS['2xs'], color: C.neutral[500] }}>keeps your roster legal</span></div>
      {list.map((m, i) => (
        <div key={i} data-testid="follow-up-move" style={{ borderTop: BORDER, padding: `7px 0`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs }}>
            <span style={pillStyle(C.success, 0.16)}>PICKUP</span>
            {m.player_in && (
              <MobilePlayerLink mlbId={m.player_in.player_id} name={m.player_in.player_name || '—'} onClick={onPlayerClick}
                style={{ fontSize: FS.sm, fontWeight: 700, color: C.white }} />
            )}
            {m.player_in?.position && <span style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{m.player_in.eligibility_display || m.player_in.position}</span>}
            {m.standings_delta != null && (
              <span style={{ marginLeft: 'auto', fontSize: FS.xs, fontWeight: 800, color: m.standings_delta > 0 ? C.success : C.neutral[500], ...NUM }}>
                {m.standings_delta > 0 ? '+' : ''}{Math.round(m.standings_delta * 10) / 10}
              </span>
            )}
          </div>
          {m.player_out && (
            <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs }}>
              <span style={pillStyle(C.error, 0.16)}>DROP</span>
              <MobilePlayerLink mlbId={m.player_out.player_id} name={m.player_out.player_name || '—'} onClick={onPlayerClick}
                style={{ fontSize: FS.sm, color: C.neutral[400] }} />
            </div>
          )}
          {/* DD-RO-MOBILE-TRADE-MODAL-0728: the follow-up's per-category PILLS are removed — they showed only
              the backfill's standings-POINT deltas (≈0 when no rank moved), which read as an implausible "all
              zero". The whole-trade "Net Category Effect" section above now carries the real per-category
              detail (raw swing + point) from the corrected reprojection. The pickup/drop players stay. */}
        </div>
      ))}
    </>
  );
};

/** FRAME A — three-team cycle. */
function ThreeTeam({ sequence, pkg, onClose, onOpen, onPlayerClick }) {
  const user = pkg.user_team_id;
  const legs = pkg.legs || [];
  const gains = pkg.counterparty_gains || {};
  const names = pkg.counterparty_names || {};
  const factors = pkg.acceptance_factors || {};
  const cpIds = Object.keys(gains);
  const shs = sectionHeaderStyle(PRIMARY);
  const node = (bg, fg, letter) => (
    <span style={{ width: 20, height: 20, flexShrink: 0, borderRadius: R.full, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: FS['2xs'], fontWeight: 900, background: bg, color: fg }}>{letter}</span>
  );
  return (
    <Shell testid="three-team-sheet" onClose={onClose}
      title="Three-team trade"
      pills={<span style={pillStyle(C.dataPurple, 0.16)}>3-Team</span>}
      footer={<Footer onClose={onClose} onOpen={onOpen} />}>
      <div style={{ display: 'flex', borderTop: BORDER, borderBottom: BORDER }} data-testid="metric-strip">
        <div style={{ flex: 1.3, textAlign: 'center', padding: `6px 2px`, background: withAlpha(C.success, 0.08) }}>
          <div style={{ fontSize: FS['2xs'], color: C.neutral[500], textTransform: 'uppercase' }}>Your Net</div>
          <div style={{ fontSize: FS.lg, fontWeight: 900, color: C.success, ...NUM }}>{pkg.net_value >= 0 ? `+${pkg.net_value}` : pkg.net_value}</div>
        </div>
        <Metric label="Both Accept" value={pct(pkg.acceptance_probability)} color={C.error} />
        <Metric label="Parties" value="3" color={C.white} />
      </div>

      {/* THE CYCLE — vertical chain on one rail, loop closes back to YOU */}
      <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>The Cycle</span>
        <span style={{ marginLeft: 'auto', fontSize: FS['2xs'], color: C.neutral[500] }}>each gives one · gets one</span></div>
      <div style={{ position: 'relative', paddingLeft: 26 }} data-testid="cycle">
        <div style={{ position: 'absolute', left: 9, top: 14, bottom: 14, width: 2, background: withAlpha(PRIMARY, 0.32) }} />
        {legs.map((lg, i) => {
          const p = (lg.players || [])[0] || {};
          const toUser = lg.to_is_user;
          return (
            <div key={i} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: SP.xs, padding: `8px 0`, borderBottom: i < legs.length - 1 ? BORDER_SOFT : 'none' }}>
              <span style={{ position: 'absolute', left: -26 }}>
                {lg.from_is_user ? node(PRIMARY, C.neutral[950], 'YOU') : node(C.neutral[700], C.neutral[300], String.fromCharCode(65 + i))}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: FS['2xs'], color: lg.from_is_user ? C.error : C.neutral[500], fontWeight: 800, textTransform: 'uppercase' }}>
                  {lg.from_is_user ? 'You send' : `${lg.from_team_name} sends`}
                </div>
                <MobilePlayerLink mlbId={p.player_id} name={p.player_name || '—'} onClick={onPlayerClick}
                  style={{ fontSize: FS.sm, fontWeight: 700, color: toUser ? C.success : C.white }} />

                <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>{[(p.eligibility_display || p.position), p.contract_salary != null ? `$${p.contract_salary}` : null].filter(Boolean).join(' · ')}</div>
              </div>
              <span style={{ color: toUser ? PRIMARY : C.neutral[600], fontSize: FS.sm }}>▸</span>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: FS['2xs'], color: toUser ? C.success : C.neutral[500], fontWeight: 800 }}>{toUser ? 'Back to you' : 'To'}</div>
                <div style={{ fontSize: FS.xs, fontWeight: toUser ? 900 : 700, color: toUser ? PRIMARY : C.neutral[300] }}>{lg.to_team_name}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: FS['2xs'], color: C.neutral[500], padding: `6px 0` }}>
        <span style={{ color: PRIMARY }}>↻</span> The loop closes — each team gives one, gets one.
      </div>

      {/* WHO GAINS — user primary, counterparties neutral/subordinate */}
      <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>Who Gains</span>
        <span style={{ marginLeft: 'auto', fontSize: FS['2xs'], color: C.neutral[500] }}>not directly comparable</span></div>
      <div style={{ borderTop: BORDER, borderBottom: BORDER }} data-testid="who-gains">
        <div style={{ display: 'flex', alignItems: 'center', gap: SP.xs, padding: `8px 0`, borderBottom: BORDER_SOFT }}>
          {node(PRIMARY, C.neutral[950], 'YOU')}
          <span style={{ flex: 1, fontSize: FS.xs, fontWeight: 800, color: C.white }}>You</span>
          <span style={{ fontSize: FS.base, fontWeight: 900, color: C.success, ...NUM }}>{pkg.net_value >= 0 ? `+${pkg.net_value}` : pkg.net_value}</span>
        </div>
        {cpIds.map((id, i) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: SP.xs, padding: `6px 0` }}>
            {node(C.neutral[700], C.neutral[300], String.fromCharCode(65 + i))}
            <span style={{ flex: 1, fontSize: FS.xs, color: C.neutral[300] }}>{names[id] || 'Counterparty'}</span>
            <span style={{ fontSize: FS.sm, fontWeight: 700, color: C.neutral[300], ...NUM }}>+{gains[id]}</span>
          </div>
        ))}
      </div>

      {/* DD-RO-ITEM3-EXECUTE-0729: per-swap sections (falls back to the whole-cycle view for N-team, which
          has no two-team swap_chain), replacing the follow-up add-ons menu. */}
      <SwapSections pkg={pkg} />

      {/* BOTH MUST AGREE — the product of per-counterparty acceptance factors */}
      {cpIds.length === 2 && (
        <>
          <div style={sectionHeaderStyle(C.error).row}><span style={sectionHeaderStyle(C.error).bar} /><span style={sectionHeaderStyle(C.error).label}>Both Must Agree</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderTop: BORDER, borderBottom: BORDER, padding: `9px 0` }} data-testid="both-must-agree">
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>A accepts</div>
              <div style={{ fontSize: FS.sm, fontWeight: 900, color: C.warning, ...NUM }}>{pct(factors[cpIds[0]])}</div>
            </div>
            <span style={{ fontSize: FS.xs, color: C.neutral[600], fontWeight: 900 }}>×</span>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>B accepts</div>
              <div style={{ fontSize: FS.sm, fontWeight: 900, color: C.warning, ...NUM }}>{pct(factors[cpIds[1]])}</div>
            </div>
            <span style={{ fontSize: FS.xs, color: C.neutral[600], fontWeight: 900 }}>=</span>
            <div style={{ textAlign: 'center', flex: 1.2, background: withAlpha(C.error, 0.08), padding: `4px 2px` }}>
              <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>both</div>
              <div style={{ fontSize: FS.base, fontWeight: 900, color: C.error, ...NUM }}>{pct(pkg.acceptance_probability)}</div>
            </div>
          </div>
          <div style={{ fontSize: FS['2xs'], color: C.neutral[400], marginTop: 6 }}>
            Two independent yeses are needed, so a three-team deal is harder to close than a two-team one.
          </div>
        </>
      )}
    </Shell>
  );
}
